/**
 * ClassQuizAdmin.js - Admin panel for uploading class quiz Excel files,
 * generating LLM answers, and reviewing/editing answers.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import { API_BASE_URL, logger, apiFetch } from '../config';
import { getCurriculumUrl } from '../App';

// Patch xlsx encode_col/encode_cell to never throw on negative indices (caused by
// full-column named ranges like A:A in xlsx 0.18.x — returns '' instead of crashing)
try {
  const _encodeCol = XLSX.utils.encode_col.bind(XLSX.utils);
  const _encodeCell = XLSX.utils.encode_cell.bind(XLSX.utils);
  XLSX.utils.encode_col = (col) => { if (col < 0) return ''; return _encodeCol(col); };
  XLSX.utils.encode_cell = (cell) => { if (cell && (cell.c < 0 || cell.r < 0)) return ''; return _encodeCell(cell); };
} catch (e) { /* ignore if patch fails */ }

// Flexible column header mapping — tries multiple possible header names
const COLUMN_MAP = {
  studentExternalId: ['StudentExternalId', 'Student External Id', 'ExternalId', 'Student ID', 'StudentID'],
  firstName: ['FirstName', 'First Name', 'First_Name', 'first_name'],
  lastName: ['LastName', 'Last Name', 'Last_Name', 'last_name'],
  username: ['Username', 'username', 'User Name', 'Email', 'email'],
  attemptNumber: ['Attempt#', 'Attempt #', 'AttemptNumber', 'Attempt Number', 'attempt_number'],
  attemptDate: ['AttemptDate', 'Attempt Date', 'Date'],
  attemptStart: ['AttemptStart', 'Attempt Start', 'Start'],
  attemptEnd: ['AttemptEnd', 'Attempt End', 'End'],
  section: ['Sections1', 'Section', 'Sections'],
  questionNumber: ['Q#', 'Q #', 'Question#', 'Question Number', 'QuestionNumber'],
  questionType: ['Q Type', 'QType', 'Question Type', 'Type'],
  questionTitle: ['Q Title', 'QTitle', 'Question Title'],
  questionText: ['Q Text', 'QText', 'Question Text', 'Question'],
  recall: ['Recall', 'recall'],
  difficulty: ['Difficulty', 'difficulty'],
  answer: ['Answer', 'answer', 'Response'],
  answerMatch: ['Answer Match', 'AnswerMatch', 'Answer_Match'],
  score: ['Score', 'score', 'Points'],
  outOf: ['Out Of', 'OutOf', 'Out_Of', 'Max Score', 'MaxScore', 'Total'],
};

function findColumn(headers, fieldName) {
  const candidates = COLUMN_MAP[fieldName] || [];
  for (const c of candidates) {
    if (headers.includes(c)) return c;
  }
  // Try case-insensitive partial match
  const lower = fieldName.toLowerCase();
  for (const h of headers) {
    if (h.toLowerCase().replace(/[_\s#]/g, '') === lower.replace(/[_\s#]/g, '')) return h;
  }
  return null;
}

function parseExcelRow(row, headerMap) {
  const get = (field) => {
    const col = headerMap[field];
    if (!col) return '';
    const val = row[col];
    return val !== undefined && val !== null ? String(val).trim() : '';
  };
  const getNum = (field) => {
    const val = get(field);
    const n = parseFloat(val);
    return isNaN(n) ? 0 : n;
  };

  // Strip everything after @ from username
  let uname = get('username');
  if (uname.includes('@')) {
    uname = uname.split('@')[0];
  }

  return {
    studentExternalId: get('studentExternalId'),
    firstName: get('firstName'),
    lastName: get('lastName'),
    username: uname,
    attemptNumber: getNum('attemptNumber') || 1,
    attemptDate: get('attemptDate'),
    attemptStart: get('attemptStart'),
    attemptEnd: get('attemptEnd'),
    section: get('section'),
    questionNumber: getNum('questionNumber'),
    questionType: get('questionType'),
    questionTitle: get('questionTitle'),
    questionText: get('questionText'),
    recall: get('recall'),
    difficulty: getNum('difficulty'),
    answer: get('answer'),
    answerMatch: get('answerMatch'),
    score: getNum('score'),
    outOf: getNum('outOf'),
  };
}

const ClassQuizAdmin = ({ selectedSection }) => {
  // Helper: clean up question text (remove excessive blank lines from \r\n artifacts)
  const cleanText = (text) => {
    if (!text) return '';
    return text
      .replace(/\r\n/g, '\n')       // normalize line endings
      .replace(/\n{3,}/g, '\n\n')   // collapse 3+ newlines to 2
      .trim();
  };

  // Helper: detect if text looks like code (used for answer display)
  const looksLikeCode = (text) => {
    if (!text) return false;
    const codeIndicators = ['print(', 'input(', 'def ', 'import ', 'for ', 'while ', 'if ', '= ', 'eval(', 'float(', 'int(', '#'];
    const lines = text.split('\n').filter(l => l.trim());
    let codeLines = 0;
    for (const line of lines) {
      if (codeIndicators.some(ind => line.includes(ind))) codeLines++;
    }
    return codeLines >= 2 || (lines.length >= 2 && text.includes('(') && text.includes(')'));
  };

  // Helper: render question text — preserves whitespace/indentation.
  const renderFormattedQuestion = (text) => {
    if (!text) return null;
    let cleaned = cleanText(text);
    
    // Strip options section if present (LLM sometimes includes them despite instructions)
    // Look for "Options:" line and remove everything from there if followed by numbered items
    const optionsMatch = cleaned.match(/\n\s*Options:\s*\n\s*1\./i);
    if (optionsMatch) {
      cleaned = cleaned.substring(0, optionsMatch.index).trim();
    }
    
    // Each line as a div, using transparent underscores for spacing
    const html = cleaned.split('\n').map(line => {
      const spaces = line.length - line.trimStart().length;
      const indent = spaces > 0 ? `<span style="visibility:hidden">${'_'.repeat(spaces)}</span>` : '';
      const rest = line.substring(spaces).replace(/</g, '&lt;').replace(/>/g, '&gt;');
      if (line.trim() === '') {
        return '<div style="height:0.8em"></div>';
      }
      return `<div style="text-align:left">${indent}${rest}</div>`;
    }).join('');
    
    return (
      <div 
        style={{
          margin: 0, 
          padding: '8px 10px',
          fontFamily: "Courier New, Consolas, monospace",
          fontSize: '14px', 
          color: '#f0f6fc', 
          backgroundColor: '#161b22',
          borderRadius: '4px', 
          border: '1px solid #30363d',
          lineHeight: '1.6',
          textAlign: 'left',
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  };
  // Formats flat Python code string (no newlines) into properly indented lines
  const formatPythonCode = (raw) => {
    if (!raw) return '(no answer)';
    // If it already has real newlines, use them directly
    if (raw.includes('\n')) return raw;
    // Keywords that start a new line
    const starters = [
      'def ', 'class ', 'if ', 'elif ', 'else:', 'else ',
      'for ', 'while ', 'try:', 'try ', 'except ', 'except:',
      'finally:', 'finally ', 'with ', 'return ', 'import ',
      'from ', 'print(', 'raise ', 'break', 'continue', 'pass',
    ];
    // Split on spaces but keep track of position
    // Strategy: scan token by token and insert newline before any starter keyword
    const tokens = raw.split(' ');
    const lines = [];
    let current = '';
    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i];
      const joined = current ? current + ' ' + token : token;
      const startsNew = starters.some(s => token.startsWith(s) || token === s.trim());
      if (startsNew && current) {
        lines.push(current);
        current = token;
      } else {
        current = joined;
      }
    }
    if (current) lines.push(current);
    // Determine indentation: lines inside if/for/while/def/else get 4 spaces
    let indent = 0;
    const result = lines.map(line => {
      const trimmed = line.trim();
      // Decrease indent for else/elif/except/finally
      if (/^(else:|elif |except |except:|finally:|finally )/.test(trimmed)) {
        indent = Math.max(0, indent - 4);
      }
      const out = ' '.repeat(indent) + trimmed;
      // Increase indent after block starters
      if (/(if |elif |else:|for |while |def |class |try:|except |except:|finally:|finally |with ).*:$/.test(trimmed)) {
        indent += 4;
      }
      return out;
    });
    return result.join('\n');
  };

  const WrCodeBlock = ({ code, color }) => (
    <pre style={{
      margin: '4px 0 0 0',
      padding: '6px 10px',
      fontFamily: 'Courier New, Consolas, monospace',
      fontSize: '12px',
      color: color,
      backgroundColor: '#0d1117',
      border: `1px solid ${color}33`,
      borderRadius: '4px',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      textAlign: 'left',
      lineHeight: '1.6',
    }}>{formatPythonCode(code)}</pre>
  );

  // Upload state
  const [dragOver, setDragOver] = useState(false);
  const [parsedData, setParsedData] = useState(null); // { quizId, classSection, rows, studentCount, questionCount }
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  // Quiz list state
  const [quizzes, setQuizzes] = useState([]);
  const [quizLoading, setQuizLoading] = useState(false);
  // Use master section from prop (set by main page dropdown)
  const filterSection = selectedSection || (localStorage.getItem('class_section') || '').split(',')[0].trim();

  // Generate answers state
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState({ current: 0, total: 0, quizId: '' });

  // Review/Edit answers state
  const [reviewQuizId, setReviewQuizId] = useState(null);
  const [reviewAnswers, setReviewAnswers] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);
  const [editAnswer, setEditAnswer] = useState('');
  const [editExplanation, setEditExplanation] = useState('');
  const [saving, setSaving] = useState(false);

  // Student Grades modal state
  const [gradesQuizId, setGradesQuizId] = useState(null);
  const [gradesData, setGradesData] = useState([]); // array of { username, firstName, lastName, score, outOf, pct, questions }
  const [gradesLoading, setGradesLoading] = useState(false);
  const [gradesExpandedStudent, setGradesExpandedStudent] = useState(null); // username of expanded student
  const [wrScoreEditing, setWrScoreEditing] = useState(null); // { username, questionNumber }
  const [wrScoreValue, setWrScoreValue] = useState(''); // current input value
  const [wrGradingLevel, setWrGradingLevel] = useState('Strict');
  const [wrGradingResults, setWrGradingResults] = useState({}); // { [username]: { [questionNumber]: { score, outOf, feedback } } }
  const [wrGradingLoading, setWrGradingLoading] = useState(null); // username currently being graded

  // Chapter lock state
  const [courseChapters, setCourseChapters] = useState([]);
  const [chapterLock, setChapterLock] = useState('');

  // Log
  const [logs, setLogs] = useState([]);
  const logRef = useRef(null);

  const addLog = useCallback((msg) => {
    const time = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, `[${time}] ${msg}`]);
    setTimeout(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, 50);
  }, []);

  // Load quiz list — fetch all, then filter client-side by section
  const loadQuizzes = useCallback(async () => {
    setQuizLoading(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/classQuiz/list`);
      if (res.ok) {
        const data = await res.json();
        let list = data.quizzes || [];
        console.log('📋 Quiz keys:', list.length > 0 ? Object.keys(list[0]) : 'empty');
        console.log('📋 First quiz:', list.length > 0 ? JSON.stringify(list[0]) : 'none');
        console.log('📋 filterSection:', JSON.stringify(filterSection));
        // Client-side filter — section is embedded in quizId (e.g. "Quiz#1-CIS3260-06-GSU")
        if (filterSection && list.length > 0) {
          const fSection = filterSection.trim().toLowerCase();
          list = list.filter(q => {
            const qId = (q.quizId || '').toLowerCase();
            return qId.includes(fSection);
          });
        }
        setQuizzes(list);
      } else {
        setQuizzes([]);
      }
    } catch (e) {
      logger.error('Failed to load quiz list:', e);
    }
    setQuizLoading(false);
  }, []);

  useEffect(() => { loadQuizzes(); }, [loadQuizzes]);

  // Reload when master section changes
  useEffect(() => { loadQuizzes(); }, [selectedSection]);

  // Load course structure for chapter lock dropdown
  useEffect(() => {
    const loadChapters = async () => {
      try {
        const res = await fetch(getCurriculumUrl('course_structure.json'));
        if (res.ok) {
          const data = await res.json();
          setCourseChapters(data || []);
        }
      } catch (e) {
        console.warn('Could not load course structure for chapter lock:', e);
      }
    };
    loadChapters();
  }, []);

  // ==================== FILE PARSING ====================
  const handleFile = (file) => {
    if (!file) return;
    const name = file.name.replace(/\.(xlsx|xls|csv)$/i, '');
    // Extract quizId and classSection from filename
    // Expected: QuizName-class-section-university (e.g. Quiz1-CIS3260-06-GSU)
    const parts = name.split('-');
    if (parts.length < 4) {
      addLog(`❌ Invalid filename: "${file.name}"`);
      addLog(`   Expected format: QuizName-class-section-university.xlsx`);
      addLog(`   Example: Quiz1-CIS3260-06-GSU.xlsx`);
      alert(`⚠️ Invalid filename format!\n\nExpected: QuizName-class-section-university.xlsx\nExample: Quiz1-CIS3260-06-GSU.xlsx\n\nYour file: ${file.name}\n\nPlease rename the file and try again.`);
      return;
    }
    let quizId = name;
    let section = parts.slice(1).join('-');

    addLog(`Parsing file: ${file.name}`);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        addLog(`📦 File bytes loaded: ${data.length}`);

        addLog(`🔧 Step 1: XLSX.read...`);
        const workbook = XLSX.read(data, { type: 'array', cellStyles: false, cellNF: false, bookVBA: false, WTF: false });
        addLog(`✅ Step 1 done. Sheets: ${workbook.SheetNames.join(', ')}`);

        // Strip named ranges that contain full-column refs (e.g. A:A)
        if (workbook.Workbook && workbook.Workbook.Names) {
          const before = workbook.Workbook.Names.length;
          workbook.Workbook.Names = workbook.Workbook.Names.filter(n => {
            try { return !String(n.Ref || '').match(/:[A-Z]+:[A-Z]+|![A-Z]+:[A-Z]+$/); } catch { return false; }
          });
          addLog(`🔧 Named ranges: ${before} total, ${workbook.Workbook.Names.length} kept after filter`);
        } else {
          addLog(`🔧 No named ranges found`);
        }

        addLog(`🔧 Step 2: Getting sheet...`);
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        addLog(`✅ Step 2 done. Sheet ref: ${sheet['!ref'] || 'none'}`);

        // Remove data validations that can also trigger encode_col(-1)
        if (sheet['!dataValidation']) { addLog(`🔧 Removing !dataValidation`); delete sheet['!dataValidation']; }
        if (sheet['!autofilter']) { addLog(`🔧 Removing !autofilter`); delete sheet['!autofilter']; }
        if (sheet['!merges']) { addLog(`🔧 Merges count: ${sheet['!merges'].length}`); }

        // Fix: recompute !ref from actual cell keys to avoid negative column refs
        // caused by named ranges / data validation in xlsx 0.18.x
        const cellKeys = Object.keys(sheet).filter(k => !k.startsWith('!'));
        if (cellKeys.length > 0) {
          let minR = Infinity, maxR = -Infinity, minC = Infinity, maxC = -Infinity;
          cellKeys.forEach(k => {
            try {
              const addr = XLSX.utils.decode_cell(k);
              if (addr.r >= 0 && addr.c >= 0) {
                if (addr.r < minR) minR = addr.r;
                if (addr.r > maxR) maxR = addr.r;
                if (addr.c < minC) minC = addr.c;
                if (addr.c > maxC) maxC = addr.c;
              }
            } catch (e) {}
          });
          if (minR !== Infinity) {
            const newRef = XLSX.utils.encode_range({ s: { r: minR, c: minC }, e: { r: maxR, c: maxC } });
            addLog(`🔧 Recomputed !ref: ${sheet['!ref']} → ${newRef}`);
            sheet['!ref'] = newRef;
          }
        }
        // Remove merges with negative column/row indices
        if (sheet['!merges']) {
          sheet['!merges'] = sheet['!merges'].filter(m =>
            m.s.c >= 0 && m.s.r >= 0 && m.e.c >= 0 && m.e.r >= 0
          );
        }

        addLog(`🔧 Step 3: sheet_to_json...`);
        let json;
        try {
          json = XLSX.utils.sheet_to_json(sheet, { defval: '' });
        } catch (innerErr) {
          // Last resort: use sheet_to_csv then parse manually
          addLog(`⚠️ sheet_to_json failed (${innerErr.message}), falling back to CSV parse...`);
          const csv = XLSX.utils.sheet_to_csv(sheet);
          const lines = csv.split('\n').filter(l => l.trim());
          const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
          json = lines.slice(1).map(line => {
            const vals = line.split(',');
            const obj = {};
            headers.forEach((h, i) => { obj[h] = (vals[i] || '').trim().replace(/^"|"$/g, ''); });
            return obj;
          });
          addLog(`📋 CSV fallback: ${json.length} rows parsed`);
        }
        addLog(`✅ Step 3 done. Rows: ${json.length}`);

        if (json.length === 0) {
          addLog('❌ No data rows found in Excel file.');
          return;
        }

        // Build header map
        const headers = Object.keys(json[0]);
        addLog(`Found ${json.length} rows, ${headers.length} columns.`);
        addLog(`Headers: ${headers.join(', ')}`);

        const headerMap = {};
        for (const field of Object.keys(COLUMN_MAP)) {
          headerMap[field] = findColumn(headers, field);
        }

        // Log mapping
        const mapped = Object.entries(headerMap).filter(([, v]) => v).map(([k, v]) => `${k}→"${v}"`).join(', ');
        const unmapped = Object.entries(headerMap).filter(([, v]) => !v).map(([k]) => k).join(', ');
        addLog(`Mapped: ${mapped}`);
        if (unmapped) addLog(`⚠️ Unmapped fields (will be empty): ${unmapped}`);

        // DEBUG: Log RAW Excel rows for M-S questions BEFORE parsing
        // Parse rows
        const rows = json.map(r => parseExcelRow(r, headerMap));

        // Stats
        const students = new Set(rows.map(r => r.username)).size;
        const questions = new Set(rows.map(r => r.questionNumber)).size;

        addLog(`✅ Parsed: ${rows.length} rows, ${students} students, ${questions} questions`);

        setParsedData({ quizId, classSection: section, rows, studentCount: students, questionCount: questions, fileName: file.name });
        setUploadResult(null);
      } catch (err) {
        addLog(`❌ Parse error: ${err.message}`);
        addLog(`📍 Stack: ${err.stack ? err.stack.split('\n').slice(0,4).join(' | ') : 'no stack'}`);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);

  // ==================== UPLOAD ====================
  const handleUpload = async () => {
    if (!parsedData) return;
    setUploading(true);
    setUploadResult(null);
    addLog(`Uploading ${parsedData.rows.length} rows as quiz "${parsedData.quizId}"...`);

    try {
      const res = await apiFetch(`${API_BASE_URL}/api/classQuiz/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quizId: parsedData.quizId,
          classSection: parsedData.classSection,
          rows: parsedData.rows
        })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        addLog(`✅ Uploaded! ${data.inserted} rows inserted.`);
        setUploadResult({ success: true, inserted: data.inserted });
        setParsedData(null);
        // Refresh quiz list to show newly uploaded quiz
        await loadQuizzes();
      } else {
        addLog(`❌ Upload failed: ${data.error || res.status}`);
        setUploadResult({ success: false, error: data.error || 'Unknown error' });
      }
    } catch (e) {
      addLog(`❌ Upload error: ${e.message}`);
      setUploadResult({ success: false, error: e.message });
    }
    setUploading(false);
  };

  // ==================== GENERATE ANSWERS ====================
  const handleGenerateAnswers = async (quizId) => {
    setGenerating(true);
    setGenProgress({ current: 0, total: 0, quizId });
    addLog(`Fetching questions for quiz "${quizId}"...`);
    if (chapterLock) {
      const lockCh = courseChapters.find(ch => ch.id === chapterLock);
      addLog(`📚 Chapter lock active: up to "${lockCh ? lockCh.title : chapterLock}"`);
    }

    try {
      // Step 1: Get unique questions from backend
      const res = await apiFetch(`${API_BASE_URL}/api/classQuiz/generateAnswers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quizId })
      });
      const data = await res.json();
      const questions = data.questions || [];
      addLog(`Found ${questions.length} unique questions.`);
      setGenProgress({ current: 0, total: questions.length, quizId });

      // Step 2: For each question, call LLM and save answer
      for (let i = 0; i < questions.length; i++) {
        const q = questions[i];
        const qNum = q.questionNumber;
        const qType = q.questionType;
        const qText = q.questionText;
        const options = q.options || [];
        const isWrittenResponse = qType && qType.toUpperCase() === 'WR';
        const isMultiSelect = qType && qType.toUpperCase() === 'M-S';

        setGenProgress({ current: i + 1, total: questions.length, quizId });
        addLog(`Generating answer for Q${qNum} (${qType})...`);

        // Build chapter constraint for the LLM prompt
        let chapterConstraint = '';
        let systemChapterRule = '';
        if (chapterLock && courseChapters.length > 0) {
          const lockIdx = courseChapters.findIndex(ch => ch.id === chapterLock);
          if (lockIdx >= 0) {
            const allowedChapters = courseChapters.slice(0, lockIdx + 1);
            const forbiddenChapters = courseChapters.slice(lockIdx + 1);
            const topicList = allowedChapters.map(ch =>
              `- ${ch.title}: ${ch.topics.join(', ')}`
            ).join('\n');
            const forbiddenList = forbiddenChapters.map(ch =>
              `- ${ch.title}: ${ch.topics.join(', ')}`
            ).join('\n');

            // Strong constraint in the system message
            systemChapterRule = `\n\nCRITICAL CURRICULUM RULE — YOU MUST OBEY THIS:\nStudents have ONLY learned material up to and including "${allowedChapters[lockIdx].title}".\n\nALLOWED topics:\n${topicList}\n\nFORBIDDEN topics (students have NOT learned these yet — do NOT use them):\n${forbiddenList}\n\nYour answer and code MUST only use concepts from the ALLOWED topics. Do NOT use functions, loops, classes, or any technique from FORBIDDEN topics. For example, if "Functions" is forbidden, do NOT define or call any custom functions (def). If "Loops" is forbidden, do NOT use for/while loops. Write the simplest solution using ONLY what students have learned.`;

            // Also reinforce in user prompt
            chapterConstraint = `\n\n[CURRICULUM LOCK: Only use concepts up to "${allowedChapters[lockIdx].title}". No functions/def, no loops, no classes, etc. unless they are in the allowed chapters above.]\n`;
          }
        }

        // Common instruction: ask LLM to also format the question text properly
        const formatInstruction = `\n\nALSO: The question text above may have Python code jammed together on one line (newlines were lost). In your JSON response, include a "formattedQuestion" field where you rewrite ONLY the question text with proper formatting:\n- Put the prose/question sentence on its own line(s)\n- Put each Python code statement on its own line with CORRECT Python indentation (e.g. code inside if/for/while/def must be indented with 4 spaces)\n- Preserve the original Python indentation structure exactly as the code would appear in a .py file\n- Do NOT include the answer options/choices in formattedQuestion — those are handled separately\n- If there is no code in the question, just return the question text as-is\n- Do NOT change the wording — only add line breaks and indentation where needed`;

        let prompt;
        if (isWrittenResponse) {
          // Written response — ignore student answers, just use question text
          prompt = `You are a Python programming expert. Answer this question with a complete, correct solution.${chapterConstraint}\n\nQuestion: ${qText}${formatInstruction}\n\nRespond in JSON format:\n{"correctAnswer": "<the correct answer or code solution>", "explanation": "<brief explanation of the solution>", "formattedQuestion": "<the question text with proper line breaks and code formatting>"}`;
        } else if (isMultiSelect && options.length > 1) {
          // Multi-select question — multiple correct answers
          const optList = options.map((o, idx) => `${idx + 1}. ${o}`).join('\n');
          prompt = `You are a Python programming expert. This is a MULTI-SELECT question where one or more options can be correct.${chapterConstraint}\n\nQuestion: ${qText}\n\nOptions:\n${optList}\n\nIMPORTANT: For correctAnswer, return ALL correct options separated by ", " (comma space). Return ONLY the option text itself, do NOT include the number prefix.${formatInstruction}\n\nRespond in JSON format:\n{"correctAnswer": "<option1, option2, ...>", "explanation": "<brief explanation of why each is correct>", "formattedQuestion": "<the question text with proper line breaks and code formatting>"}`;
        } else if (options.length > 1) {
          // Multi-option question (MC, T/F, M-S, etc.)
          const optList = options.map((o, idx) => `${idx + 1}. ${o}`).join('\n');
          prompt = `You are a Python programming expert. Answer this question.${chapterConstraint}\n\nQuestion: ${qText}\n\nOptions:\n${optList}\n\nIMPORTANT: For correctAnswer, return the EXACT text of the correct option as shown above, WITHOUT the leading number and period. For example, if the correct option is "3. 2 1", return "2 1" not "3" and not "3. 2 1".${formatInstruction}\n\nRespond in JSON format:\n{"correctAnswer": "<exact option text, no number prefix>", "explanation": "<brief explanation of why>", "formattedQuestion": "<the question text with proper line breaks and code formatting>"}`;
        } else {
          // Other open-ended
          prompt = `You are a Python programming expert. Answer this question with a complete solution.${chapterConstraint}\n\nQuestion: ${qText}${formatInstruction}\n\nRespond in JSON format:\n{"correctAnswer": "<the correct answer or code solution>", "explanation": "<brief explanation>", "formattedQuestion": "<the question text with proper line breaks and code formatting>"}`;
        }

        try {
          const llmRes = await apiFetch(`${API_BASE_URL}/api/chat`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              model: 'gpt-4.1',
              messages: [
                { role: 'system', content: `You are a Python expert. Always respond with valid JSON only.${systemChapterRule}` },
                { role: 'user', content: prompt }
              ],
              temperature: 0.3,
              max_tokens: 1000,
              response_format: { type: 'json_object' }
            })
          });

          if (!llmRes.ok) {
            addLog(`⚠️ LLM error for Q${qNum}: ${llmRes.status}`);
            continue;
          }

          const llmData = await llmRes.json();
          let content = llmData.choices[0].message.content.trim();
          content = content.replace(/^```json\s*/i, '').replace(/\s*```$/i, '');
          const parsed = JSON.parse(content);

          let correctAnswer = parsed.correctAnswer || parsed.correct_answer || parsed.answer || '';
          // Strip leading number prefix like "2. " or "3) " from MC answers (not WR, not M-S)
          // Must require a dot or paren after the digit — NOT just a space (e.g. "2 1" is a valid answer)
          if (!isWrittenResponse && !isMultiSelect) {
            correctAnswer = correctAnswer.replace(/^\d+[.)]\s*/, '');
          }
          if (isMultiSelect) {
            // Clean each part of comma-separated M-S answers
            correctAnswer = correctAnswer.split(',').map(s => s.trim().replace(/^\d+[.)]\s*/, '')).filter(Boolean).join(', ');
          }

          // Post-processing: if the answer doesn't match any option, the LLM may have
          // returned a number (option index). Resolve it to the actual option text.
          if (!isWrittenResponse && options.length > 0) {
            const answerTrimmed = correctAnswer.trim();
            const matchesOption = options.some(o => o.trim() === answerTrimmed);
            if (!matchesOption) {
              // Check if it's a pure number (option index, 1-based)
              const asNum = parseInt(answerTrimmed, 10);
              if (!isNaN(asNum) && asNum >= 1 && asNum <= options.length) {
                const resolved = options[asNum - 1].trim();
                addLog(`⚠️ Q${qNum}: LLM returned "${answerTrimmed}" (option #${asNum}), resolved to "${resolved}"`);
                correctAnswer = resolved;
              } else {
                // Try partial match — answer might be contained in an option or vice versa
                const partialMatch = options.find(o => o.trim().includes(answerTrimmed) || answerTrimmed.includes(o.trim()));
                if (partialMatch) {
                  addLog(`⚠️ Q${qNum}: LLM answer "${answerTrimmed}" partially matched option "${partialMatch.trim()}"`);
                  correctAnswer = partialMatch.trim();
                }
              }
            }
          }

          // Extract formattedQuestion from LLM response
          const formattedQuestion = parsed.formattedQuestion || parsed.formatted_question || '';

          // Encode formattedQuestion into the explanation field using a delimiter
          // so it persists through the existing backend with ZERO backend changes
          const explanationRaw = parsed.explanation || '';
          const explanationToSave = formattedQuestion
            ? explanationRaw + '<!--FQ-->' + formattedQuestion
            : explanationRaw;

          // Save to backend (don't store student answers as options for WR)
          await apiFetch(`${API_BASE_URL}/api/classQuiz/saveAnswer`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              quizId,
              questionNumber: qNum,
              questionType: qType,
              questionText: qText,
              options: isWrittenResponse ? '[]' : JSON.stringify(options),
              correctAnswer: correctAnswer,
              explanation: explanationToSave
            })
          });

          addLog(`✅ Q${qNum}: ${correctAnswer.substring(0, 60)}...`);
        } catch (llmErr) {
          addLog(`⚠️ Error on Q${qNum}: ${llmErr.message}`);
        }
      }

      addLog(`✅ Done generating answers for "${quizId}".`);
      loadQuizzes();
    } catch (e) {
      addLog(`❌ Generate error: ${e.message}`);
    }
    setGenerating(false);
  };

  // Helper: decode formattedQuestion embedded in explanation via <!--FQ--> delimiter
  const decodeFormattedQuestion = (explanation) => {
    if (!explanation || !explanation.includes('<!--FQ-->')) {
      return { explanation: explanation || '', formattedQuestion: '' };
    }
    const parts = explanation.split('<!--FQ-->');
    return { explanation: parts[0], formattedQuestion: parts[1] || '' };
  };

  // ==================== REVIEW / EDIT ANSWERS ====================
  const handleReviewAnswers = async (quizId) => {
    setReviewQuizId(quizId);
    setReviewLoading(true);
    setEditingIdx(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/classQuiz/answers?quizId=${encodeURIComponent(quizId)}`);
      if (res.ok) {
        const data = await res.json();
        // Decode formattedQuestion from each answer's explanation field
        const answers = (data.answers || []).map(a => {
          const decoded = decodeFormattedQuestion(a.explanation);
          return { ...a, explanation: decoded.explanation, formattedQuestion: decoded.formattedQuestion };
        });
        setReviewAnswers(answers);
      } else {
        addLog(`❌ Failed to load answers for ${quizId}`);
      }
    } catch (e) {
      addLog(`❌ Error loading answers: ${e.message}`);
    }
    setReviewLoading(false);
  };

  const handleStartEdit = (idx) => {
    setEditingIdx(idx);
    setEditAnswer(reviewAnswers[idx].correctAnswer || '');
    setEditExplanation(reviewAnswers[idx].explanation || '');
  };

  const handleSaveEdit = async (idx) => {
    setSaving(true);
    const a = reviewAnswers[idx];
    // Re-encode formattedQuestion back into explanation when saving edits
    const explanationToSave = a.formattedQuestion
      ? editExplanation + '<!--FQ-->' + a.formattedQuestion
      : editExplanation;
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/classQuiz/updateAnswer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quizId: reviewQuizId,
          questionNumber: a.questionNumber,
          correctAnswer: editAnswer,
          explanation: explanationToSave
        })
      });
      if (res.ok) {
        addLog(`✅ Updated Q${a.questionNumber} answer.`);
        const updated = [...reviewAnswers];
        updated[idx] = { ...updated[idx], correctAnswer: editAnswer, explanation: editExplanation };
        setReviewAnswers(updated);
        setEditingIdx(null);
      } else {
        addLog(`❌ Failed to update Q${a.questionNumber}`);
      }
    } catch (e) {
      addLog(`❌ Save error: ${e.message}`);
    }
    setSaving(false);
  };

  // ==================== DELETE QUIZ ====================
  const handleDeleteQuiz = async (quizId) => {
    if (!window.confirm(`Delete quiz "${quizId}" and all its answers? This cannot be undone.`)) return;
    addLog(`Deleting quiz "${quizId}"...`);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/classQuiz/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quizId })
      });
      if (res.ok) {
        addLog(`✅ Deleted "${quizId}".`);
        if (reviewQuizId === quizId) { setReviewQuizId(null); setReviewAnswers([]); }
        loadQuizzes();
      }
    } catch (e) {
      addLog(`❌ Delete error: ${e.message}`);
    }
  };

  // ==================== STUDENT GRADES ====================
  const handleViewGrades = async (quizId) => {
    setGradesQuizId(quizId);
    setGradesLoading(true);
    setGradesData([]);
    setGradesExpandedStudent(null);
    try {
      const [studentsRes, answersRes] = await Promise.all([
        apiFetch(`${API_BASE_URL}/api/classQuiz/allStudentResults?quizId=${encodeURIComponent(quizId)}`),
        apiFetch(`${API_BASE_URL}/api/classQuiz/answers?quizId=${encodeURIComponent(quizId)}`)
      ]);
      // Build answer key map
      let answerMap = {};
      if (answersRes.ok) {
        const ansData = await answersRes.json();
        for (const a of (ansData.answers || [])) {
          let explanation = a.explanation || '';
          let formattedQuestion = '';
          if (explanation.includes('<!--FQ-->')) {
            const parts = explanation.split('<!--FQ-->');
            explanation = parts[0];
            formattedQuestion = parts[1] || '';
          }
          answerMap[a.questionNumber] = { ...a, explanation, formattedQuestion };
        }
      }
      if (studentsRes.ok) {
        const data = await studentsRes.json();
        const students = (data.students || []).map(s => {
          // Merge answer key into each question
          const questions = (s.questions || []).map(q => ({
            ...q,
            feedback: q.feedback || '',
            ...(answerMap[q.questionNumber] ? {
              options: q.options || answerMap[q.questionNumber].options,
              correctAnswer: q.correctAnswer || answerMap[q.questionNumber].correctAnswer,
              explanation: q.explanation || answerMap[q.questionNumber].explanation,
              formattedQuestion: q.formattedQuestion || answerMap[q.questionNumber].formattedQuestion,
            } : {})
          }));
          const totalScore = questions.reduce((sum, q) => sum + (parseFloat(q.score) || 0), 0);
          const totalOutOf = questions.reduce((sum, q) => sum + (parseFloat(q.outOf) || 0), 0);
          const pct = totalOutOf > 0 ? Math.round((totalScore / totalOutOf) * 100) : 0;
          return { ...s, questions, score: totalScore, outOf: totalOutOf, pct };
        });
        // Sort by last name
        students.sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''));
        setGradesData(students);
      } else {
        addLog(`❌ Failed to load student grades for ${quizId}`);
      }
    } catch (e) {
      addLog(`❌ Grades error: ${e.message}`);
    }
    setGradesLoading(false);
  };

  const handleSaveWrScore = async (username, questionNumber, newScore, outOf) => {
    const clamped = Math.max(0, Math.min(parseFloat(newScore), outOf));
    if (isNaN(clamped)) { setWrScoreEditing(null); return; }
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/classQuiz/updateWrScore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quizId: gradesQuizId, username, questionNumber, score: clamped }),
      });
      if (res.ok) {
        // Update gradesData in place
        setGradesData(prev => prev.map(s => {
          if (s.username !== username) return s;
          const questions = s.questions.map(q =>
            q.questionNumber === questionNumber ? { ...q, score: clamped } : q
          );
          const totalScore = questions.reduce((sum, q) => sum + (parseFloat(q.score) || 0), 0);
          const totalOutOf = questions.reduce((sum, q) => sum + (parseFloat(q.outOf) || 0), 0);
          const pct = totalOutOf > 0 ? Math.round((totalScore / totalOutOf) * 100) : 0;
          return { ...s, questions, score: totalScore, outOf: totalOutOf, pct };
        }));
        addLog(`✅ Updated Q${questionNumber} score for ${username} → ${clamped}/${outOf}`);
      } else {
        addLog(`❌ Failed to update score for ${username} Q${questionNumber}`);
      }
    } catch (e) {
      addLog(`❌ Score update error: ${e.message}`);
    }
    setWrScoreEditing(null);
  };

  // ==================== WR GRADING ====================
  const gradeStrictnessInstructions = (level) => {
    switch (level) {
      case 'Very Strict': return 'Grade very strictly. Only award full marks if the code is essentially identical in logic and style. Minor naming differences or extra spaces should lose points. Partial credit only for nearly-correct solutions. Syntax errors (missing colons, wrong indentation, typos in keywords, unclosed parentheses, etc.) should lose significant points.';
      case 'Strict': return 'Grade strictly. Award full marks only if the logic is correct and the code is clean. Small variable name differences are ok, but logical errors should lose significant points. Syntax errors (missing colons, wrong indentation, typos in keywords, unclosed parentheses, etc.) should lose points.';
      case 'Lenient': return 'Grade leniently. Award full marks if the core logic is correct, even if variable names differ or there are minor style issues. Give partial credit generously. Minor syntax errors (missing colons, small typos) should only lose a small amount of points if the logic is sound.';
      case 'Very Lenient': return 'Grade very leniently. Award full marks if the student demonstrates understanding of the concept, even if the code has minor bugs or style differences. Give partial credit for any reasonable attempt. Minor syntax errors should be overlooked if the student clearly understands the concept.';
      default: return '';
    }
  };

  const handleGradeWR = async (student) => {
    setWrGradingLoading(student.username);
    const wrQuestions = student.questions.filter(q => q.questionType === 'WR' && q.studentAnswer);
    const results = {};
    for (const q of wrQuestions) {
      try {
        const prompt = `You are grading a Python programming quiz question.\n\nStrictness level: ${wrGradingLevel}\n${gradeStrictnessInstructions(wrGradingLevel)}\n\nAlso check for syntax errors such as: missing colons, incorrect indentation, typos in keywords, unclosed parentheses/brackets, missing quotes, invalid variable names, etc. Mention any syntax errors found in your feedback.\n\nQuestion: ${q.questionText || q.formattedQuestion}\n\nCorrect Answer:\n${q.correctAnswer}\n\nStudent Answer:\n${q.studentAnswer}\n\nMax points: ${q.outOf}\n\nRespond with JSON only in this exact format: {"score": <number>, "outOf": ${q.outOf}, "feedback": "<one sentence explanation>"}`;
        const res = await apiFetch(`${API_BASE_URL}/api/gradeText`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prompt }),
        });
        if (res.ok) {
          const data = await res.json();
          const reply = data.result || '';
          const jsonMatch = reply.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            const parsed = JSON.parse(jsonMatch[0]);
            results[q.questionNumber] = {
              score: parseFloat(parsed.score) || 0,
              outOf: q.outOf,
              feedback: parsed.feedback || '',
            };
          }
        }
      } catch (e) {
        addLog(`❌ WR grading error for Q${q.questionNumber}: ${e.message}`);
      }
    }
    setWrGradingResults(prev => ({ ...prev, [student.username]: results }));
    setWrGradingLoading(null);
  };

  const handleSaveWrGrades = async (student) => {
    const results = wrGradingResults[student.username];
    if (!results) return;
    for (const [qNum, result] of Object.entries(results)) {
      try {
        const payload = {
          quizId: gradesQuizId,
          username: student.username,
          questionNumber: parseInt(qNum),
          score: result.score,
          feedback: result.feedback || '',
        };
        console.log('💾 saveWrGrade payload:', JSON.stringify(payload));
        addLog(`Saving Q${qNum}: score=${payload.score}, feedback="${payload.feedback}"`);
        const res = await apiFetch(`${API_BASE_URL}/api/classQuiz/saveWrGrade`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (res.ok) {
          setGradesData(prev => prev.map(s => {
            if (s.username !== student.username) return s;
            const questions = s.questions.map(q =>
              q.questionNumber === parseInt(qNum)
                ? { ...q, score: result.score, feedback: result.feedback }
                : q
            );
            const totalScore = questions.reduce((sum, q) => sum + (parseFloat(q.score) || 0), 0);
            const totalOutOf = questions.reduce((sum, q) => sum + (parseFloat(q.outOf) || 0), 0);
            const pct = totalOutOf > 0 ? Math.round((totalScore / totalOutOf) * 100) : 0;
            return { ...s, questions, score: totalScore, outOf: totalOutOf, pct };
          }));
        } else {
          addLog(`❌ Failed to save grade for Q${qNum}`);
        }
      } catch (e) {
        addLog(`❌ Save error Q${qNum}: ${e.message}`);
      }
    }
    addLog(`✅ Saved AI grades for ${student.username}`);
    setWrGradingResults(prev => { const n = { ...prev }; delete n[student.username]; return n; });
  };

  // ==================== RENDER ====================
  return (
    <div style={{ padding: '20px', height: '100%', overflow: 'auto', color: '#c9d1d9' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
        <h2 style={{ color: '#aff5b4', margin: 0 }}>📋 Class Quiz Manager</h2>
        <span style={{ color: '#f0d060', fontSize: '13px', fontWeight: 'bold' }}>📌 {filterSection}</span>
      </div>

      {/* Upload Area */}
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        style={{
          border: `2px dashed ${dragOver ? '#aff5b4' : '#ffd766'}`,
          borderRadius: '10px',
          padding: '24px',
          textAlign: 'center',
          backgroundColor: dragOver ? 'rgba(175,245,180,0.05)' : 'rgba(13,17,23,0.5)',
          marginBottom: '16px',
          cursor: 'pointer',
          transition: 'all 0.2s',
        }}
        onClick={() => {
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = '.xlsx,.xls,.csv';
          input.onchange = (e) => handleFile(e.target.files[0]);
          input.click();
        }}
      >
        <div style={{ fontSize: '28px', marginBottom: '8px' }}>📄</div>
        <div style={{ color: '#c9d1d9', fontSize: '14px' }}>
          Drag & drop Excel file here, or click to browse
        </div>
        <div style={{ color: '#c9d1d9', fontSize: '12px', marginTop: '4px' }}>
          Filename format: <code style={{ color: '#aff5b4' }}>QuizName-class-section-university.xlsx</code>
        </div>
        <div style={{ color: '#c9d1d9', fontSize: '11px', marginTop: '2px' }}>
          Example: <code style={{ color: '#c9d1d9' }}>Quiz1-CIS3260-06-GSU.xlsx</code>
        </div>
      </div>

      {/* Parsed Preview */}
      {parsedData && (
        <div style={{
          backgroundColor: 'rgba(175,245,180,0.05)',
          border: '1px solid #aff5b4',
          borderRadius: '8px',
          padding: '14px',
          marginBottom: '16px',
        }}>
          <div style={{ fontWeight: 'bold', color: '#aff5b4', marginBottom: '8px' }}>📊 Preview</div>
          <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
            <div>📁 File: <strong>{parsedData.fileName}</strong></div>
            <div>🆔 Quiz ID: <strong>{parsedData.quizId}</strong></div>
            <div>🏫 Class Section: <strong>{parsedData.classSection}</strong></div>
            <div>📝 Rows: <strong>{parsedData.rows.length}</strong></div>
            <div>👥 Students: <strong>{parsedData.studentCount}</strong></div>
            <div>❓ Questions: <strong>{parsedData.questionCount}</strong></div>
          </div>
          <button
            onClick={handleUpload}
            disabled={uploading}
            style={{
              marginTop: '10px',
              padding: '8px 24px',
              backgroundColor: '#238636',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: uploading ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              fontWeight: 'bold',
            }}
          >
            {uploading ? '⏳ Uploading...' : '⬆️ Upload to Database'}
          </button>

          {/* Upload Spinner */}
          {uploading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '14px' }}>
              <div style={{
                width: '28px',
                height: '28px',
                border: '3px solid #21262d',
                borderTop: '3px solid #1f6feb',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }} />
              <span style={{ color: '#c9d1d9', fontSize: '13px' }}>Uploading {parsedData?.rows?.length || ''} rows...</span>
              <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
          )}
        </div>
      )}

      {/* Upload Result */}
      {uploadResult && (
        <div style={{
          padding: '10px 14px',
          borderRadius: '6px',
          marginBottom: '16px',
          backgroundColor: uploadResult.success ? 'rgba(46,160,67,0.15)' : 'rgba(248,81,73,0.15)',
          border: `1px solid ${uploadResult.success ? '#2ea043' : '#f85149'}`,
          fontSize: '13px',
        }}>
          {uploadResult.success
            ? `✅ Successfully uploaded ${uploadResult.inserted} rows!`
            : `❌ Upload failed: ${uploadResult.error}`
          }
        </div>
      )}

      {/* Quiz List */}
      <div style={{ marginTop: '60px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
          <h3 style={{ color: '#aff5b4', fontSize: '15px', margin: 0 }}>📚 Uploaded Quizzes</h3>
          {filterSection && <span style={{ color: '#8b949e', fontSize: '12px' }}>Section: {filterSection}</span>}
          <button
            onClick={loadQuizzes}
            style={{
              background: 'none', border: '1px solid #30363d',
              color: '#8b949e', borderRadius: '4px', padding: '4px 12px', cursor: 'pointer', fontSize: '11px'
            }}
          >🔄 Refresh</button>
        </div>

        {quizLoading && <div style={{ color: '#8b949e', fontSize: '13px' }}>Loading quizzes...</div>}

        {!quizLoading && quizzes.length === 0 && (
          <div style={{ color: '#6e7681', fontSize: '13px' }}>{filterSection ? 'No quizzes found for this section.' : 'Enter a class section and click Refresh.'}</div>
        )}

        {quizzes.map((q, idx) => (
          <div key={idx} style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 14px',
            backgroundColor: '#161b22',
            border: '1px solid rgba(175, 245, 180, 0.25)',
            borderRadius: '6px',
            marginBottom: '6px',
          }}>
            <div>
              <span style={{ color: '#c9d1d9', fontWeight: 'bold', fontSize: '13px' }}>{q.quizId}</span>
              <span style={{ color: '#c9d1d9', fontSize: '12px', marginLeft: '12px' }}>
                👥 {q.studentCount} students · ❓ {q.questionCount} questions
                {q.hasAnswers && <span style={{ color: '#2ea043', marginLeft: '8px' }}>✅ Answers generated</span>}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '5px',
                padding: '3px 8px', borderRadius: '5px',
                border: '1px solid rgba(255, 215, 102, 0.4)',
                backgroundColor: 'rgba(255, 215, 102, 0.05)'
              }}>
                <span style={{ fontSize: '11px', color: '#ffd766', fontWeight: 'bold', whiteSpace: 'nowrap' }}>📚 Knowledge Limit:</span>
                <select
                  value={chapterLock}
                  onChange={(e) => setChapterLock(e.target.value)}
                  style={{
                    padding: '3px 6px', fontSize: '11px', borderRadius: '4px',
                    backgroundColor: '#0d1117', color: chapterLock ? '#ffd700' : '#e6edf3',
                    border: `1px solid ${chapterLock ? '#ffd700' : 'rgba(255, 215, 102, 0.3)'}`, cursor: 'pointer',
                    maxWidth: '200px', fontWeight: chapterLock ? 'bold' : 'normal'
                  }}
                  title="Restrict LLM to only use concepts up to this chapter"
                >
                  <option value="">No Limit</option>
                  {courseChapters.map((ch) => {
                    const slideNum = ch.id.replace('slide', 'Slide ');
                    return (
                      <option key={ch.id} value={ch.id}>
                        Up to {slideNum}: {ch.title.length > 22 ? ch.title.substring(0, 22) + '…' : ch.title}
                      </option>
                    );
                  })}
                </select>
              </div>
              <button
                onClick={() => handleGenerateAnswers(q.quizId)}
                disabled={generating}
                style={{
                  padding: '4px 12px', fontSize: '11px', borderRadius: '4px',
                  backgroundColor: '#1f6feb', color: '#fff', border: 'none', cursor: 'pointer'
                }}
              >
                {generating && genProgress.quizId === q.quizId
                  ? `⏳ ${genProgress.current}/${genProgress.total}`
                  : '🤖 Generate Answers'
                }
              </button>
              <button
                onClick={() => handleReviewAnswers(q.quizId)}
                disabled={!q.hasAnswers}
                style={{
                  padding: '4px 12px', fontSize: '11px', borderRadius: '4px',
                  backgroundColor: q.hasAnswers ? '#238636' : '#21262d',
                  color: q.hasAnswers ? '#fff' : '#484f58',
                  border: 'none', cursor: q.hasAnswers ? 'pointer' : 'not-allowed'
                }}
              >📝 Review/Edit</button>
              <button
                onClick={() => handleViewGrades(q.quizId)}
                style={{
                  padding: '4px 12px', fontSize: '11px', borderRadius: '4px',
                  backgroundColor: '#0d1b30', color: '#58a6ff',
                  border: '1px solid #58a6ff', cursor: 'pointer'
                }}
              >📊 Student Grades</button>
              <button
                onClick={() => handleDeleteQuiz(q.quizId)}
                style={{
                  padding: '4px 12px', fontSize: '11px', borderRadius: '4px',
                  backgroundColor: 'transparent', color: '#f85149', border: '1px solid #f85149', cursor: 'pointer'
                }}
              >🗑️ Delete</button>
            </div>
          </div>
        ))}
      </div>

      {/* Review / Edit Answers Panel */}
      {reviewQuizId && (
        <div style={{
          backgroundColor: '#161b22',
          border: '1px solid #30363d',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
          textAlign: 'left',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ color: '#aff5b4', fontSize: '14px', margin: 0 }}>
              📝 Answers for: {reviewQuizId}
            </h3>
            <button
              onClick={() => { setReviewQuizId(null); setReviewAnswers([]); setEditingIdx(null); }}
              style={{ background: 'none', border: 'none', color: '#f0f6fc', cursor: 'pointer', fontSize: '22px', fontWeight: 'bold' }}
              onMouseOver={(e) => e.target.style.color = '#f85149'}
              onMouseOut={(e) => e.target.style.color = '#f0f6fc'}
            >✕</button>
          </div>

          {reviewLoading && <div style={{ color: '#8b949e', fontSize: '13px' }}>Loading answers...</div>}

          {!reviewLoading && reviewAnswers.length === 0 && (
            <div style={{ color: '#6e7681', fontSize: '13px' }}>No answers generated yet. Click "Generate Answers" first.</div>
          )}

          {reviewAnswers.map((a, idx) => (
            <div key={idx} style={{
              padding: '12px',
              backgroundColor: '#0d1117',
              border: '1px solid #21262d',
              borderRadius: '6px',
              marginBottom: '8px',
              textAlign: 'left',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ color: '#58a6ff', fontWeight: 'bold', fontSize: '13px' }}>
                  Q{a.questionNumber} ({a.questionType})
                </span>
                {editingIdx !== idx && (
                  <button
                    onClick={() => handleStartEdit(idx)}
                    style={{
                      padding: '2px 8px', fontSize: '11px', borderRadius: '4px',
                      backgroundColor: 'transparent', color: '#58a6ff', border: '1px solid #58a6ff', cursor: 'pointer'
                    }}
                  >✏️ Edit</button>
                )}
              </div>

              <div style={{ color: '#d0d7de', fontSize: '12px', marginBottom: '6px', lineHeight: '1.5' }}>
                {renderFormattedQuestion(a.formattedQuestion || a.questionText)}
              </div>

              {a.options && (() => {
                try {
                  const opts = JSON.parse(a.options);
                  if (Array.isArray(opts) && opts.length > 0) {
                    return (
                      <div style={{ marginBottom: '16px' }}>
                        {opts.map((o, oi) => (
                          <div key={oi} style={{
                            fontSize: '12px', color: '#d0d7de', paddingLeft: '12px'
                          }}>• {o}</div>
                        ))}
                      </div>
                    );
                  }
                } catch (e) { /* ignore */ }
                return null;
              })()}

              {editingIdx === idx ? (
                <div>
                  <div style={{ fontSize: '12px', color: '#aff5b4', marginBottom: '4px' }}>Correct Answer:</div>
                  <textarea
                    value={editAnswer}
                    onChange={(e) => setEditAnswer(e.target.value)}
                    style={{
                      width: '100%', minHeight: '60px', backgroundColor: '#0d1117', color: '#c9d1d9',
                      border: '1px solid #30363d', borderRadius: '4px', padding: '6px', fontSize: '12px',
                      fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box'
                    }}
                  />
                  <div style={{ fontSize: '12px', color: '#aff5b4', marginTop: '6px', marginBottom: '4px' }}>Explanation:</div>
                  <textarea
                    value={editExplanation}
                    onChange={(e) => setEditExplanation(e.target.value)}
                    style={{
                      width: '100%', minHeight: '40px', backgroundColor: '#0d1117', color: '#c9d1d9',
                      border: '1px solid #30363d', borderRadius: '4px', padding: '6px', fontSize: '12px',
                      fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box'
                    }}
                  />
                  <div style={{ marginTop: '6px', display: 'flex', gap: '6px' }}>
                    <button
                      onClick={() => handleSaveEdit(idx)}
                      disabled={saving}
                      style={{
                        padding: '4px 12px', fontSize: '11px', borderRadius: '4px',
                        backgroundColor: '#238636', color: '#fff', border: 'none', cursor: 'pointer'
                      }}
                    >{saving ? '⏳ Saving...' : '💾 Save'}</button>
                    <button
                      onClick={() => setEditingIdx(null)}
                      style={{
                        padding: '4px 12px', fontSize: '11px', borderRadius: '4px',
                        backgroundColor: 'transparent', color: '#8b949e', border: '1px solid #30363d', cursor: 'pointer'
                      }}
                    >Cancel</button>
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                    <span style={{ color: '#2ea043', fontWeight: 'bold' }}>Answer: </span>
                    {looksLikeCode(a.correctAnswer) ? (
                      <pre style={{
                        backgroundColor: '#161b22',
                        border: '1px solid #30363d',
                        borderRadius: '6px',
                        padding: '10px 12px',
                        margin: '4px 0 0 0',
                        fontSize: '15px',
                        fontFamily: "'Courier New', Courier, monospace",
                        color: '#ffd700',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        lineHeight: '1.6',
                        overflow: 'auto',
                        textAlign: 'left',
                      }}>{cleanText(a.correctAnswer)}</pre>
                    ) : (
                      <span style={{ color: '#d0d7de', whiteSpace: 'pre-wrap' }}>{cleanText(a.correctAnswer)}</span>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', marginTop: '6px' }}>
                    <span style={{ color: '#d2a8ff' }}>Explanation: </span>
                    <span style={{ color: '#d0d7de', whiteSpace: 'pre-wrap' }}>{cleanText(a.explanation)}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Student Grades Modal */}
      {gradesQuizId && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 4000,
          display: 'flex', justifyContent: 'center', alignItems: 'center',
        }}>
          <div style={{
            width: '85%', maxWidth: '900px', maxHeight: '85vh',
            backgroundColor: '#0d1117', border: '1px solid rgba(175,245,180,0.3)',
            borderRadius: '12px', display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{
              padding: '16px 20px', borderBottom: '1px solid #21262d',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              backgroundColor: '#161b22',
            }}>
              <h3 style={{ color: '#aff5b4', margin: 0, fontSize: '16px' }}>📊 Student Grades — {gradesQuizId}</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#f0d060', fontSize: '14px', fontWeight: 'bold' }}>WR Grading:</span>
                <select
                  value={wrGradingLevel}
                  onChange={e => setWrGradingLevel(e.target.value)}
                  style={{
                    backgroundColor: '#1e2530', color: '#fff5b0',
                    border: '1px solid #f0d060', borderRadius: '6px',
                    padding: '5px 10px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer',
                  }}
                >
                  <option>Very Strict</option>
                  <option>Strict</option>
                  <option>Lenient</option>
                  <option>Very Lenient</option>
                </select>
                <button
                  onClick={() => { setGradesQuizId(null); setGradesData([]); setGradesExpandedStudent(null); setWrGradingResults({}); }}
                  style={{ background: 'none', border: 'none', color: '#f0f6fc', cursor: 'pointer', fontSize: '22px', fontWeight: 'bold' }}
                  onMouseOver={(e) => e.target.style.color = '#f85149'}
                  onMouseOut={(e) => e.target.style.color = '#f0f6fc'}
                >✕</button>
              </div>
            </div>

            {/* Body */}
            <div style={{ overflowY: 'auto', padding: '16px' }}>
              {gradesLoading && <div style={{ color: '#8b949e', fontSize: '13px' }}>Loading student grades...</div>}

              {!gradesLoading && gradesData.length === 0 && (
                <div style={{ color: '#6e7681', fontSize: '13px' }}>No student submissions found for this quiz.</div>
              )}

              {!gradesLoading && gradesData.length > 0 && (
                <>
                  {/* Summary row */}
                  <div style={{ color: '#8b949e', fontSize: '12px', marginBottom: '12px' }}>
                    {gradesData.length} student{gradesData.length !== 1 ? 's' : ''} · Avg: {Math.round(gradesData.reduce((s, st) => s + st.pct, 0) / gradesData.length)}%
                  </div>

                  {/* Table header */}
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 80px 80px 60px 80px',
                    gap: '8px', padding: '6px 10px',
                    backgroundColor: '#161b22', borderRadius: '6px',
                    fontSize: '11px', fontWeight: 'bold', color: '#8b949e',
                    marginBottom: '4px',
                  }}>
                    <span>Username</span>
                    <span>Last Name</span>
                    <span>First Name</span>
                    <span>Score</span>
                    <span>Out Of</span>
                    <span>%</span>
                    <span></span>
                  </div>

                  {/* Student rows */}
                  {gradesData.map((s, i) => (
                    <div key={i}>
                      <div style={{
                        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 80px 80px 60px 80px',
                        gap: '8px', padding: '8px 10px',
                        backgroundColor: '#161b22', borderRadius: '6px',
                        marginBottom: '2px', alignItems: 'center',
                        border: '1px solid #21262d',
                      }}>
                        <span style={{ color: '#8b949e', fontSize: '12px' }}>{s.username}</span>
                        <span style={{ color: '#c9d1d9', fontSize: '13px' }}>{s.lastName || '—'}</span>
                        <span style={{ color: '#c9d1d9', fontSize: '13px' }}>{s.firstName || '—'}</span>
                        <span style={{ color: '#c9d1d9', fontSize: '13px' }}>{s.score.toFixed(1)}</span>
                        <span style={{ color: '#c9d1d9', fontSize: '13px' }}>{s.outOf.toFixed(1)}</span>
                        <span style={{
                          fontSize: '13px', fontWeight: 'bold',
                          color: s.pct >= 80 ? '#7ee787' : s.pct >= 60 ? '#f0883e' : '#f85149'
                        }}>{s.pct}%</span>
                        <button
                          onClick={() => setGradesExpandedStudent(gradesExpandedStudent === s.username ? null : s.username)}
                          style={{
                            padding: '3px 8px', fontSize: '11px', borderRadius: '4px',
                            backgroundColor: 'transparent', color: '#58a6ff',
                            border: '1px solid #58a6ff', cursor: 'pointer'
                          }}
                        >{gradesExpandedStudent === s.username ? '▲ Hide' : '👁 View'}</button>
                      </div>

                      {/* Expanded: individual Q&A */}
                      {gradesExpandedStudent === s.username && (
                        <div style={{
                          backgroundColor: '#0d1117', border: '1px solid #30363d',
                          borderRadius: '6px', padding: '12px', marginBottom: '6px',
                        }}>
                          {/* Grade WR button — only if student has WR answers */}
                          {s.questions.some(q => q.questionType === 'WR' && q.studentAnswer) && (
                            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <button
                                onClick={() => handleGradeWR(s)}
                                disabled={wrGradingLoading === s.username}
                                style={{
                                  padding: '5px 14px', fontSize: '12px', borderRadius: '4px',
                                  backgroundColor: wrGradingLoading === s.username ? '#21262d' : '#1a3a2a',
                                  color: wrGradingLoading === s.username ? '#8b949e' : '#aff5b4',
                                  border: '1px solid #aff5b4', cursor: wrGradingLoading === s.username ? 'not-allowed' : 'pointer',
                                }}
                              >{wrGradingLoading === s.username ? '⏳ Grading...' : `🤖 Grade WR with AI (${wrGradingLevel})`}</button>
                              {wrGradingResults[s.username] && (<>
                                <button
                                  onClick={() => handleSaveWrGrades(s)}
                                  style={{
                                    padding: '5px 14px', fontSize: '12px', borderRadius: '4px',
                                    backgroundColor: '#0d2818', color: '#aff5b4',
                                    border: '1px solid #3fb950', cursor: 'pointer',
                                  }}
                                >💾 Save AI Grades</button>
                                <button
                                  onClick={() => setWrGradingResults(prev => { const n = { ...prev }; delete n[s.username]; return n; })}
                                  style={{
                                    padding: '5px 10px', fontSize: '12px', borderRadius: '4px',
                                    backgroundColor: 'transparent', color: '#8b949e',
                                    border: '1px solid #30363d', cursor: 'pointer',
                                  }}
                                >✕ Clear</button>
                              </>)}
                            </div>
                          )}

                          {/* AI Grade Preview Banner */}
                          {wrGradingResults[s.username] && (() => {
                            const aiResults = wrGradingResults[s.username];
                            // Merge AI results with existing scores for a full preview
                            const previewTotal = s.questions.reduce((sum, q) => {
                              const ai = aiResults[q.questionNumber];
                              return sum + (ai ? parseFloat(ai.score) || 0 : parseFloat(q.score) || 0);
                            }, 0);
                            const previewOutOf = s.questions.reduce((sum, q) => sum + (parseFloat(q.outOf) || 0), 0);
                            const previewPct = previewOutOf > 0 ? Math.round((previewTotal / previewOutOf) * 100) : 0;
                            const currentPct = s.pct;
                            const diff = previewPct - currentPct;
                            return (
                              <div style={{
                                backgroundColor: '#0d2818', border: '1px solid #3fb950',
                                borderRadius: '6px', padding: '10px 14px', marginBottom: '12px',
                                display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
                              }}>
                                <span style={{ color: '#aff5b4', fontWeight: 'bold', fontSize: '13px' }}>
                                  🤖 Proposed Grade:
                                </span>
                                <span style={{ color: '#fff', fontSize: '15px', fontWeight: 'bold' }}>
                                  {previewTotal.toFixed(1)} / {previewOutOf.toFixed(1)}
                                </span>
                                <span style={{
                                  fontSize: '15px', fontWeight: 'bold',
                                  color: previewPct >= 80 ? '#7ee787' : previewPct >= 60 ? '#f0883e' : '#f85149',
                                }}>
                                  {previewPct}%
                                </span>
                                {diff !== 0 && (
                                  <span style={{ fontSize: '12px', color: diff > 0 ? '#7ee787' : '#f85149' }}>
                                    ({diff > 0 ? '+' : ''}{diff}% vs current {currentPct}%)
                                  </span>
                                )}
                                <span style={{ fontSize: '11px', color: '#8b949e', marginLeft: 'auto' }}>
                                  Save or Clear below to confirm
                                </span>
                              </div>
                            );
                          })()}

                          {s.questions.map((q, qi) => {
                            const aiResult = wrGradingResults[s.username]?.[q.questionNumber];
                            const isCorrect = aiResult ? aiResult.score >= aiResult.outOf * 0.5 : q.score > 0;
                            const isEditingThis = wrScoreEditing?.username === s.username && wrScoreEditing?.questionNumber === q.questionNumber;
                            return (
                              <div key={qi} style={{
                                borderLeft: `3px solid ${isCorrect ? '#7ee787' : '#f85149'}`,
                                paddingLeft: '10px', marginBottom: '10px',
                              }}>
                                <div style={{ color: '#58a6ff', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  Q{q.questionNumber} ({q.questionType}) ·
                                  {q.questionType === 'WR' && isEditingThis ? (
                                    <>
                                      <input
                                        type="number"
                                        value={wrScoreValue}
                                        min={0}
                                        max={q.outOf}
                                        step={0.5}
                                        autoFocus
                                        onChange={e => setWrScoreValue(e.target.value)}
                                        onKeyDown={e => {
                                          if (e.key === 'Enter') handleSaveWrScore(s.username, q.questionNumber, wrScoreValue, q.outOf);
                                          if (e.key === 'Escape') setWrScoreEditing(null);
                                        }}
                                        onBlur={() => handleSaveWrScore(s.username, q.questionNumber, wrScoreValue, q.outOf)}
                                        style={{
                                          width: '52px', padding: '2px 5px', fontSize: '12px',
                                          backgroundColor: '#0d1117', color: '#fff',
                                          border: '1px solid #58a6ff', borderRadius: '4px',
                                        }}
                                      />
                                      <span style={{ color: '#8b949e' }}>/ {q.outOf} pts</span>
                                      <span style={{ color: '#8b949e', fontSize: '10px' }}>(Enter to save · Esc to cancel)</span>
                                    </>
                                  ) : (
                                    <span
                                      title={q.questionType === 'WR' ? 'Click to edit score' : undefined}
                                      onClick={() => {
                                        if (q.questionType === 'WR') {
                                          setWrScoreEditing({ username: s.username, questionNumber: q.questionNumber });
                                          setWrScoreValue(String(q.score));
                                        }
                                      }}
                                      style={{
                                        cursor: q.questionType === 'WR' ? 'pointer' : 'default',
                                        textDecoration: q.questionType === 'WR' ? 'underline dotted' : 'none',
                                      }}
                                    >
                                      {aiResult
                                        ? <span style={{ color: '#aff5b4' }}> 🤖 {aiResult.score}/{aiResult.outOf} pts</span>
                                        : <span> {isCorrect ? '✅' : '❌'} {q.score}/{q.outOf} pts{q.questionType === 'WR' ? ' ✏️' : ''}</span>
                                      }
                                    </span>
                                  )}
                                </div>
                                <div style={{ color: '#c9d1d9', fontSize: '12px', marginBottom: '4px' }}>
                                  {renderFormattedQuestion(q.formattedQuestion || q.questionText)}
                                </div>
                                <div style={{ fontSize: '12px', marginBottom: '2px' }}>
                                  <span style={{ color: '#8b949e' }}>Student: </span>
                                  <span style={{ color: isCorrect ? '#7ee787' : '#f85149', fontWeight: 'bold' }}>{q.studentAnswer || '(no answer)'}</span>
                                </div>
                                {!isCorrect && q.correctAnswer && (
                                  <div style={{ fontSize: '12px', marginBottom: '2px' }}>
                                    <span style={{ color: '#8b949e' }}>Correct: </span>
                                    {q.questionType === 'WR'
                                      ? <WrCodeBlock code={q.correctAnswer} color='#7ee787' />
                                      : <span style={{ color: '#7ee787', fontWeight: 'bold' }}>{q.correctAnswer}</span>
                                    }
                                  </div>
                                )}
                                {(aiResult?.feedback || q.feedback) && (
                                  <div style={{
                                    fontSize: '11px', marginTop: '4px',
                                    backgroundColor: '#0d2818', border: '1px solid #1a4d30',
                                    borderRadius: '4px', padding: '4px 8px',
                                    color: '#aff5b4',
                                  }}>
                                    💬 {aiResult?.feedback || q.feedback}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Log Area */}
      {logs.length > 0 && (
        <div style={{
          backgroundColor: '#0d1117',
          border: '1px solid #21262d',
          borderRadius: '6px',
          padding: '10px',
          maxHeight: '200px',
          overflow: 'auto',
          fontSize: '14px',
          fontFamily: 'monospace',
          color: '#c9d1d9',
        }} ref={logRef}>
          {logs.map((l, i) => (
            <div key={i} style={{
              color: l.includes('❌') ? '#f85149' : l.includes('⚠️') ? '#f0883e' : l.includes('✅') ? '#3fb950' : l.includes('Expected format') || l.includes('Example:') ? '#f0883e' : '#c9d1d9',
              fontWeight: l.includes('❌') || l.includes('⚠️') || l.includes('Expected format') || l.includes('Example:') ? 'bold' : 'normal',
            }}>{l}</div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClassQuizAdmin;
